import React, { useRef, useEffect, useState } from 'react';
import { arrayOf, shape, string } from 'prop-types';
import classnames from 'classnames';
import { isMobile } from 'util/viewport';
import smoothScroll from './helpers';
import styles from './styles';
import { stripHtml } from 'oxygen-lib-ui/helpers/strings';

function SubsectionNav(props) {
  const listRef = useRef(null);
  const { menuItems } = props;
  const isMobileViewport = isMobile(1024);
  const [navPosition, setNavPosition] = useState(null);
  const [currentSection, setCurrentSection] = useState(null);
  const shouldStickToTop = navPosition < 0;

  useEffect(() => {
    // set callback for nav position
    function updateNavPosition(entries) {
      entries.forEach(entry => {
        if (entry.target.classList.contains('Post')) {
          if (entry.isIntersecting === false) {
            setNavPosition(1000);
          } else {
            setNavPosition(entry.target.getBoundingClientRect().y);
          }
        } else {
          setNavPosition(entry.target.getBoundingClientRect().y);
        }
      });
    }

    // set callback for section position
    function updateCurrentSection(entries) {
      const currentSectionId = entries[0].target.dataset.roundupid;
      const currentSectionIndex = menuItems.findIndex(
        item => item.roundupId === currentSectionId
      );

      if (isMobileViewport) {
        const scroller = listRef.current;
        const scrollLeft = Math.floor(
          scroller.scrollWidth * (currentSectionIndex / menuItems.length)
        );
        smoothScroll(scroller, scrollLeft, true);
      }

      setCurrentSection(currentSectionId);
    }

    // set up Intersection Observer API options
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 1.0
    };

    // create observers
    const navObserver = new IntersectionObserver(updateNavPosition);
    const sectionObserver = new IntersectionObserver(
      updateCurrentSection,
      options
    );

    // observe nav to set callback
    navObserver.observe(document.querySelector('.Post'));
    navObserver.observe(document.querySelector('.Post__recipeRoundupNav'));

    if (!isMobileViewport) {
      return () => {};
    }

    // if mobile observe sections to get scroll position
    menuItems.forEach(({ roundupId }) => {
      const el = document.querySelector(`#recipe-roundup--${roundupId}`);
      if (el) {
        sectionObserver.observe(el);
      }
    });

    return function cleanup() {
      // disconnect observers
      navObserver.disconnect(document.querySelector('.Post'));
      navObserver.disconnect(document.querySelector('.Post__recipeRoundupNav'));

      if (isMobileViewport) {
        menuItems.forEach(({ roundupId }) => {
          const el = document.querySelector(`#recipe-roundup--${roundupId}`);
          if (el) {
            sectionObserver.disconnect(el);
          }
        });
      }
    };
  }, [isMobileViewport, menuItems]);

  return (
    <nav
      className={classnames(
        'Post__recipeRoundupNav',
        'Post__recipeRoundupNav--yellowButtons',
        {
          'Post__recipeRoundupNav--sticky': shouldStickToTop
        }
      )}
    >
      <div className="Post__recipeRoundupNav--container">
        <ul className="subsectionList" ref={listRef}>
          {menuItems.map((item, index) => (
            <li
              key={item.title}
              className={currentSection === item.roundupId && 'active'}
            >
              <a
                href={`#recipe-roundup--${item.roundupId}`}
                dangerouslySetInnerHTML={{
                  __html: `${index + 1}: ${item.title}`
                }}
                data-gtm-recipe-roundup-id={item.roundupId}
                data-gtm-recipe-roundup-title={stripHtml(item.title)}
              />
            </li>
          ))}
        </ul>
      </div>
      <style jsx>{styles}</style>
    </nav>
  );
}

SubsectionNav.propTypes = {
  menuItems: arrayOf(
    shape({
      roundupId: string.isRequired,
      title: string.isRequired
    })
  ).isRequired
};

export default SubsectionNav;
